import React, { useState, useMemo, useEffect, useCallback } from "react";
import debounce from "lodash/debounce";
import { Modal, ModalBody, Row, Col, Card, CardBody, Label, Button, Badge, Table, Spinner } from "reactstrap";
import { IoCloseSharp } from "react-icons/io5";
import { 
    FiBox, FiLink2, FiTag, FiLayers, FiList, FiTruck, FiPlus, 
    FiTrash2, FiImage, FiChevronUp, FiChevronDown, FiCheckCircle, 
    FiSearch, FiInfo, FiEdit2, FiCheck, FiArrowRight, FiMove,
    FiFolder, FiCornerDownRight
} from "react-icons/fi";
import { HiSparkles } from "react-icons/hi";
import { 
    createProductGroup, 
    updateProductGroup, 
    migrateToGroup,
    humanizeProductDescription, 
    getProductGroup, 
    addVariantToGroup, 
    removeVariantFromGroup, 
    bulkEditVariantAttributes,
    reorderVariants
} from "../../api/products.api";
import { getAllProducts, getAllManufacturers, getAllSubCategories, getAllCategories } from "../../api/pagination";
import toast from "react-hot-toast";
import FormInput from "../formInput/FormInput";
import GeneralButton from "../button/GeneralButton";
import { Select, Tooltip, Spin, Input as AntInput, TreeSelect } from "antd";
import { FaQuestionCircle, FaPlus } from "react-icons/fa";
import { useSelector, useDispatch } from "react-redux";
import { allManufacturers, AllCategories, AllSubCategories } from "../../store/actions/actions";
import { productTypes } from "../../data";
import ReactQuill from "react-quill";
import { formatAIResponseToHTML, uploadImage, uniqueID, generateCustomID, saveData } from "../../firebase/utility";
import AddNewManufacturer from "./AddNewManufacturer";

const SUGGESTED_ATTRIBUTES = ["capacity", "color", "size", "speed", "interface", "condition", "generation", "form_factor"];

const slugify = (text) => {
    if (!text || typeof text !== "string") return "";
    return text
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, "")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-");
};

const parseVariantAttributes = (v) => {
    if (!v || typeof v !== "object") return {};
    const raw =
        v.variantAttributes ||
        v.variant_attributes ||
        v.attributes ||
        v.variant_attribute_values ||
        v.variantAttributeValues ||
        v.variant_values ||
        v.attribute_values ||
        v.pivot?.variant_attributes ||
        v.pivot?.attributes ||
        v.item_specification ||
        {};

    if (typeof raw === "string") {
        const trimmed = raw.trim();
        if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
            try {
                const parsed = JSON.parse(trimmed);
                if (typeof parsed === "object" && parsed !== null) return parsed;
            } catch (e) {}
        }
        // Format: "key:value,key2:value2" or "Color:Black, Size:Large"
        const result = {};
        trimmed.split(",").forEach((pair) => {
            const parts = pair.split(":");
            if (parts.length >= 2) {
                const k = parts[0].trim();
                const val = parts.slice(1).join(":").trim();
                if (k) result[k] = val;
            }
        });
        if (Object.keys(result).length > 0) return result;
    }

    if (Array.isArray(raw)) {
        const result = {};
        raw.forEach((item) => {
            if (item && typeof item === "object") {
                const k = item.key || item.name || item.attribute || item.attributeKey || item.attr_key || item.title;
                const val = item.value || item.val || item.attributeValue || item.attr_value || item.description || "";
                if (k) result[String(k).trim()] = String(val).trim();
            }
        });
        return result;
    }

    if (typeof raw === "object" && raw !== null) {
        return { ...raw };
    }

    return {};
};

const getAttributeValue = (attributes, attrKey) => {
    if (!attributes || typeof attributes !== "object") return "";
    if (attributes[attrKey] !== undefined && attributes[attrKey] !== null && String(attributes[attrKey]).trim() !== "") {
        return String(attributes[attrKey]);
    }

    const lowerKey = String(attrKey).trim().toLowerCase();
    if (attributes[lowerKey] !== undefined && attributes[lowerKey] !== null && String(attributes[lowerKey]).trim() !== "") {
        return String(attributes[lowerKey]);
    }

    // Fuzzy matching against special characters / whitespace / punctuation
    const cleanTarget = lowerKey.replace(/[^a-z0-9]/g, "");
    if (cleanTarget) {
        for (const [k, val] of Object.entries(attributes)) {
            if (val !== undefined && val !== null && String(val).trim() !== "") {
                const cleanK = String(k).trim().toLowerCase().replace(/[^a-z0-9]/g, "");
                if (cleanK === cleanTarget || (cleanK.length > 2 && (cleanK.includes(cleanTarget) || cleanTarget.includes(cleanK)))) {
                    return String(val);
                }
            }
        }
    }

    return "";
};

const CreateProductGroupModal = ({ isOpen, toggle, refreshData, editData, setEditData }) => {
    const dispatch = useDispatch();
    const { all_manufacturers, all_categories, all_subCategories } = useSelector((state) => state.redux);

    const isEditMode = Boolean(editData);
    const [groupType, setGroupType] = useState("variant");
    const isVariant = groupType === "variant";
    const isRelated = groupType === "related";
    const itemTypeLabel = isRelated ? "Related Product" : "Variant";
    const itemsPluralLabel = isRelated ? "Related Products" : "Variants";
    const groupTypeLabel = isRelated ? "Related Group" : "Variant Group";

    const [loading, setLoading] = useState(false);
    const [loadingProgressText, setLoadingProgressText] = useState("");
    const [fetchingDetails, setFetchingDetails] = useState(false);
    const [humanizing, setHumanizing] = useState(false);

    // Manufacturer search & Quick-Add modal
    const [mfg, setMfg] = useState(null);
    const [searchedMfg, setSearchMfg] = useState([]);
    const [dataLoading, setDataLoading] = useState(false);
    const [mfgModal, setMfgModal] = useState(false);
    const [mfgSubmitLoading, setMfgSubmitLoading] = useState(false);


    // Basic Info Payload
    const [payload, setPayload] = useState({
        name: "",
        slug: "",
        mfg_id: "",
        productType: "",
        categoryId: "",
        subCategoryId: "",
        description: "",
        long_description: "",
        imageUrl: "",
        tempImageFile: null
    });

    // Primary Product state for Auto-Fill
    const [primaryProduct, setPrimaryProduct] = useState(null);
    const [searchingPrimary, setSearchingPrimary] = useState(false);
    const [primarySearchResults, setPrimarySearchResults] = useState([]);

    // Variant Attribute Keys (e.g. ['capacity', 'color'])
    const [attributeKeys, setAttributeKeys] = useState(["capacity"]);
    const [newAttributeInput, setNewAttributeInput] = useState("");

    // Staged Products in Group (variants or related products)
    // Array of { productId, product: Object, attributes: { [key]: value }, isExisting: boolean, isPrimary: boolean }
    const [stagedProducts, setStagedProducts] = useState([]);
    const [deletedProductIds, setDeletedProductIds] = useState([]);

    // Additional Product Search & Add picker
    const [productSearchResults, setProductSearchResults] = useState([]);
    const [searchingProducts, setSearchingProducts] = useState(false);

    // Ensure all manufacturers, categories, and subcategories are in Redux
    useEffect(() => {
        if (!all_manufacturers || all_manufacturers.length === 0) {
            getAllManufacturers(1, "", 2000, (res) => {
                if (res?.status === 200 && (res?.data?.data || res?.data)) {
                    dispatch(allManufacturers(res.data.data || res.data));
                }
            });
        }
        if (!all_categories || all_categories.length === 0) {
            getAllCategories(1, "", 2000, (res) => {
                if (res?.status === 200 && (res?.data?.data || res?.data)) {
                    dispatch(AllCategories(res.data.data || res.data));
                }
            });
        }
        if (!all_subCategories || all_subCategories.length === 0) {
            getAllSubCategories(1, "", 2000, (res) => {
                if (res?.status === 200 && (res?.data?.data || res?.data)) {
                    dispatch(AllSubCategories(res.data.data || res.data));
                }
            });
        }
    }, [all_manufacturers, all_categories, all_subCategories, dispatch]);

    // Manufacturer search debounced
    const fetchManufacturersDebounced = useMemo(
        () =>
            debounce((searchText) => {
                if (!searchText || !searchText.trim()) {
                    setSearchMfg([]);
                    setDataLoading(false);
                    return;
                }
                getAllManufacturers(1, searchText, 50, (res) => {
                    if (res?.status === 200 && res?.data?.data) {
                        setSearchMfg(res.data.data);
                    }
                    setDataLoading(false);
                });
            }, 500),
        []
    );

    const handleManufacturerSearch = (searchText) => {
        if (!searchText || !searchText.trim()) {
            setSearchMfg([]);
            setDataLoading(false);
            return;
        }
        setDataLoading(true);
        fetchManufacturersDebounced(searchText);
    };

    const handleMfg = (val) => {
        let source = searchedMfg?.length > 0 ? searchedMfg : all_manufacturers;
        let res = source?.find((i) => (i?._id && String(i._id) === String(val)) || (i?.id && String(i.id) === String(val)));
        setMfg(res);
        handleChange("mfg_id", res?._id || res?.id || val);
    };

    // Product search debounced for Primary Product and Additional Products
    const fetchProductsDebounced = useMemo(
        () =>
            debounce((query, target) => {
                if (!query || !query.trim()) {
                    if (target === "primary") {
                        setPrimarySearchResults([]);
                        setSearchingPrimary(false);
                    } else {
                        setProductSearchResults([]);
                        setSearchingProducts(false);
                    }
                    return;
                }
                getAllProducts(1, query, 40, "publish", (res) => {
                    const data = res?.data?.data || res?.data || [];
                    if (target === "primary") {
                        setPrimarySearchResults(data);
                        setSearchingPrimary(false);
                    } else {
                        setProductSearchResults(data);
                        setSearchingProducts(false);
                    }
                });
            }, 400),
        []
    );

    const handleSearchPrimary = (query) => {
        if (!query || !query.trim()) {
            setPrimarySearchResults([]);
            setSearchingPrimary(false);
            return;
        }
        setSearchingPrimary(true);
        fetchProductsDebounced(query, "primary");
    };

    const handleSearchAdditionalProducts = (query) => {
        if (!query || !query.trim()) {
            setProductSearchResults([]);
            setSearchingProducts(false);
            return;
        }
        setSearchingProducts(true);
        fetchProductsDebounced(query, "additional");
    };

    // Helper to extract category IDs from subcategory object
    const getCategoryIdsFromSubCat = (s) => {
        const ids = [];
        if (!s) return ids;
        const addId = (val) => {
            if (val === undefined || val === null || val === "") return;
            if (typeof val === "object") {
                if (val._id) ids.push(String(val._id).trim().toLowerCase());
                if (val.id) ids.push(String(val.id).trim().toLowerCase());
                if (val.categoryName) ids.push(String(val.categoryName).trim().toLowerCase());
                if (val.name) ids.push(String(val.name).trim().toLowerCase());
            } else {
                ids.push(String(val).trim().toLowerCase());
            }
        };
        addId(s.categoryId);
        addId(s.category_id);
        addId(s.category);
        addId(s.parentCategory);
        addId(s.parent_category);
        addId(s.categoryName);
        return ids;
    };

    const isSubInCat = (sub, cat) => {
        if (!sub || !cat) return false;
        const subCatIds = getCategoryIdsFromSubCat(sub);
        const catKeys = [
            cat._id ? String(cat._id).trim().toLowerCase() : "",
            cat.id ? String(cat.id).trim().toLowerCase() : "",
            cat.categoryName ? String(cat.categoryName).trim().toLowerCase() : "",
            cat.category ? String(cat.category).trim().toLowerCase() : "",
            cat.name ? String(cat.name).trim().toLowerCase() : ""
        ].filter(Boolean);

        return catKeys.some((k) => subCatIds.includes(k));
    };

    // Helper to auto-resolve Product Type (Hardware/Software) from SubCategory
    const getProductTypeFromSubCat = (s, cats = all_categories, subs = all_subCategories) => {
        if (!s) return "";
        let sub = typeof s === "object" ? s : null;
        if (!sub && subs && subs.length > 0) {
            sub = subs.find((item) => (item._id && String(item._id) === String(s)) || (item.id && String(item.id) === String(s)));
        }
        if (sub) {
            if (sub.categoryType) {
                return String(sub.categoryType).toLowerCase() === "software" ? "Software" : "Hardware";
            }
            const catIds = getCategoryIdsFromSubCat(sub);
            if (catIds.length > 0 && cats && cats.length > 0) {
                const matchedCat = cats.find((c) => (c._id && catIds.includes(String(c._id))) || (c.id && catIds.includes(String(c.id))));
                if (matchedCat?.categoryType) {
                    return String(matchedCat.categoryType).toLowerCase() === "software" ? "Software" : "Hardware";
                }
            }
        }
        return "Hardware";
    };

    // Auto-fill from Primary Product
    const handleSelectPrimaryProduct = (productId) => {
        const prod = primarySearchResults.find((p) => (p._id || p.id) === productId);
        if (!prod) return;
        setPrimaryProduct(prod);

        // 1. Resolve SubCategory from primary product
        const rawSub = prod?.subCategory || prod?.subCategoryId || prod?.sub_category || prod?.subCategoryName || "";
        const rawSubId = (rawSub && typeof rawSub === "object") ? String(rawSub._id || rawSub.id || "").trim() : String(rawSub || "").trim();
        const rawSubName = (rawSub && typeof rawSub === "object") 
            ? String(rawSub.subCategoryName || rawSub.name || "").trim()
            : (prod?.subCategoryName ? String(prod.subCategoryName).trim() : (isNaN(rawSub) ? String(rawSub || "").trim() : ""));

        // Match against all_subCategories
        const subList = (all_subCategories && all_subCategories.length > 0) ? all_subCategories : [];
        let matchedSubCat = subList.find((s) => {
            if (!s) return false;
            const sId = s._id ? String(s._id) : "";
            const sCustomId = s.id ? String(s.id) : "";
            const sName = String(s.subCategoryName || s.name || "").toLowerCase().trim();

            if (rawSubId && (sId === rawSubId || sCustomId === rawSubId)) return true;
            if (rawSubName && sName && (sName === rawSubName.toLowerCase() || sName.includes(rawSubName.toLowerCase()) || rawSubName.toLowerCase().includes(sName))) return true;
            return false;
        });

        if (!matchedSubCat && typeof prod?.subCategory === "object" && prod?.subCategory) {
            matchedSubCat = prod.subCategory;
        }

        const targetSubCatId = matchedSubCat ? (matchedSubCat._id || matchedSubCat.id || rawSubId) : rawSubId;

        // 2. Resolve Category from prod or matchedSubCat
        const rawCat = prod?.category || prod?.categoryId || prod?.category_id || "";
        let rawCatId = (rawCat && typeof rawCat === "object") ? String(rawCat._id || rawCat.id || "").trim() : String(rawCat || "").trim();

        // If category is not directly present on product, extract from matched subcategory
        if (!rawCatId && matchedSubCat) {
            const catIdCandidates = getCategoryIdsFromSubCat(matchedSubCat);
            if (catIdCandidates.length > 0) {
                rawCatId = catIdCandidates[0];
            }
        }

        const catList = (all_categories && all_categories.length > 0) ? all_categories : [];
        let matchedCat = catList.find((c) => {
            if (!c) return false;
            const cId = c._id ? String(c._id) : "";
            const cCustomId = c.id ? String(c.id) : "";
            return rawCatId && (cId === rawCatId || cCustomId === rawCatId);
        });

        const targetCatId = matchedCat ? (matchedCat._id || matchedCat.id || rawCatId) : rawCatId;

        // 3. Resolve Product Type (Hardware / Software)
        let pType = prod?.productType || "";
        if (!pType && matchedCat?.categoryType) {
            pType = matchedCat.categoryType.toLowerCase() === "software" ? "Software" : "Hardware";
        }
        if (!pType && matchedSubCat) {
            if (matchedSubCat.categoryType) {
                pType = matchedSubCat.categoryType.toLowerCase() === "software" ? "Software" : "Hardware";
            } else if (matchedCat?.categoryType) {
                pType = matchedCat.categoryType.toLowerCase() === "software" ? "Software" : "Hardware";
            } else {
                pType = "Hardware";
            }
        }
        if (!pType) {
            pType = "Hardware";
        }

        // 4. Resolve Manufacturer
        let mfgId = prod?.mfg_id || prod?.mfgDetails?._id || prod?.mfgDetails?.id || prod?.mfg || "";
        const mfgList = (all_manufacturers && all_manufacturers.length > 0) ? all_manufacturers : (searchedMfg || []);
        const prodMfg = prod.mfgDetails || mfgList.find((m) => (m._id && String(m._id) === String(mfgId)) || (m.id && String(m.id) === String(mfgId)));
        if (prodMfg) {
            setMfg(prodMfg);
            mfgId = prodMfg._id || prodMfg.id || mfgId;
        }

        // 5. Pre-fill payload
        setPayload((prev) => {
            const prodName = prod.title || prod.name || "";
            return {
                ...prev,
                name: prev.name || prodName,
                slug: prev.slug || slugify(prodName),
                mfg_id: mfgId || prev.mfg_id,
                productType: pType || prev.productType,
                categoryId: targetCatId || prev.categoryId,
                subCategoryId: targetSubCatId || prev.subCategoryId,
                description: prev.description || prod.description || "",
                long_description: prev.long_description || prod.long_description || prod.longDescription || "",
                imageUrl: prev.imageUrl || prod.imageUrl || prod.image || prod.images?.[0] || ""
            };
        });

        // Stage the Primary Product as Item #1
        setStagedProducts((prev) => {
            const alreadyExists = prev.some((p) => (p.product._id || p.product.id || p.productId) === productId);
            if (alreadyExists) return prev;

            const initialAttrs = {};
            attributeKeys.forEach((k) => {
                initialAttrs[k] = "";
            });

            return [
                {
                    productId,
                    product: prod,
                    attributes: initialAttrs,
                    isExisting: false,
                    isPrimary: true
                },
                ...prev
            ];
        });

        toast.success(`Group auto-filled from "${prod.title || prod.name || 'Primary Product'}"!`);
    };

    // Staging / Adding additional products
    const handleAddProductToStaged = (productId) => {
        if (!productId) return;
        const alreadyStaged = stagedProducts.some((p) => (p.product._id || p.product.id || p.productId) === productId);
        if (alreadyStaged) {
            toast.error("This product is already added to the group list.");
            return;
        }

        const prod = productSearchResults.find((p) => (p._id || p.id) === productId);
        if (!prod) return;

        const initialAttrs = {};
        attributeKeys.forEach((k) => {
            initialAttrs[k] = "";
        });

        setStagedProducts((prev) => [
            ...prev,
            {
                productId,
                product: prod,
                attributes: initialAttrs,
                isExisting: false,
                isPrimary: prev.length === 0
            }
        ]);
    };

    const handleRemoveStagedProduct = (productId) => {
        const itemToRemove = stagedProducts.find((p) => (p.product._id || p.product.id || p.productId) === productId);
        if (itemToRemove && itemToRemove.isExisting) {
            setDeletedProductIds((prev) => [...prev, productId]);
        }
        setStagedProducts((prev) => prev.filter((p) => (p.product._id || p.product.id || p.productId) !== productId));
        if (primaryProduct && (primaryProduct._id || primaryProduct.id) === productId) {
            setPrimaryProduct(null);
        }
    };

    const handleProductAttributeChange = (productId, attrKey, val) => {
        setStagedProducts((prev) =>
            prev.map((item) => {
                const currentId = item.product._id || item.product.id || item.productId;
                if (currentId === productId) {
                    const lowerKey = String(attrKey).trim().toLowerCase();
                    return {
                        ...item,
                        attributes: {
                            ...item.attributes,
                            [attrKey]: val,
                            [lowerKey]: val
                        }
                    };
                }
                return item;
            })
        );
    };

    // Apply attribute value to all staged products
    const handleApplyToAll = (attrKey) => {
        const lowerKey = String(attrKey).trim().toLowerCase();
        const firstVal = stagedProducts[0]?.attributes?.[attrKey] ?? stagedProducts[0]?.attributes?.[lowerKey];
        if (!firstVal) {
            toast.error(`Please enter a value for "${attrKey}" in the first row to copy to all.`);
            return;
        }
        setStagedProducts((prev) =>
            prev.map((item) => ({
                ...item,
                attributes: {
                    ...item.attributes,
                    [attrKey]: firstVal,
                    [lowerKey]: firstVal
                }
            }))
        );
        toast.success(`Applied "${firstVal}" for "${attrKey}" across all products!`);
    };

    const [draggedIndex, setDraggedIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);

    const handleDragStart = (e, index) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = "move";
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        if (dragOverIndex !== index) {
            setDragOverIndex(index);
        }
    };

    const handleDrop = (e, index) => {
        e.preventDefault();
        if (draggedIndex !== null && draggedIndex !== index) {
            moveProduct(draggedIndex, index);
        }
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    const moveProduct = (fromIdx, toIdx) => {
        if (toIdx < 0 || toIdx >= stagedProducts.length) return;
        setStagedProducts((prev) => {
            const next = [...prev];
            const [moved] = next.splice(fromIdx, 1);
            next.splice(toIdx, 0, moved);
            return next;
        });
    };

    // Attribute Key Tag Management
    const handleAddAttributeKey = (keyName) => {
        const clean = (keyName || newAttributeInput || "").trim().toLowerCase();
        if (!clean) return;
        if (attributeKeys.includes(clean)) {
            toast.error(`Attribute "${clean}" is already added.`);
            return;
        }
        const updatedKeys = [...attributeKeys, clean];
        setAttributeKeys(updatedKeys);
        setNewAttributeInput("");

        // Initialize empty value for new attribute key on all staged products
        setStagedProducts((prev) =>
            prev.map((item) => ({
                ...item,
                attributes: {
                    ...item.attributes,
                    [clean]: item.attributes?.[clean] || ""
                }
            }))
        );
    };

    const handleRemoveAttributeKey = (keyToRemove) => {
        setAttributeKeys((prev) => prev.filter((k) => k !== keyToRemove));
        setStagedProducts((prev) =>
            prev.map((item) => {
                const nextAttrs = { ...item.attributes };
                delete nextAttrs[keyToRemove];
                return {
                    ...item,
                    attributes: nextAttrs
                };
            })
        );
    };

    // Populate data when editing an existing group
    useEffect(() => {
        if (editData && isOpen) {
            const initialGroupType = editData.groupType || "variant";
            setGroupType(initialGroupType);

            const populateGroupData = (data) => {
                const extractId = (val) => {
                    if (!val && val !== 0) return "";
                    if (typeof val === "string" || typeof val === "number") return String(val).trim();
                    if (typeof val === "object") return String(val._id || val.id || "").trim();
                    return "";
                };

                const catId = extractId(data.category) || extractId(data.categoryId);
                const subCatId = extractId(data.subCategory) || extractId(data.subCategoryId);
                const mfgId = extractId(data.mfg) || extractId(data.manufacturer) || extractId(data.mfg_id) || extractId(data.mfgId);

                const matchedSub = all_subCategories?.find((s) => (s._id && String(s._id) === String(subCatId)) || (s.id && String(s.id) === String(subCatId))) || (typeof data.subCategory === "object" ? data.subCategory : null);
                const resolvedCatId = catId || extractId(matchedSub?.categoryId) || extractId(matchedSub?.category) || "";
                const matchedCat = all_categories?.find((c) => (c._id && String(c._id) === String(resolvedCatId)) || (c.id && String(c.id) === String(resolvedCatId))) || (typeof data.category === "object" ? data.category : null);

                let pType = data.productType || "";
                if (!pType) {
                    if (matchedCat?.categoryType) pType = matchedCat.categoryType;
                    else if (matchedSub?.categoryType) pType = matchedSub.categoryType;
                    else if (matchedSub || subCatId) pType = getProductTypeFromSubCat(matchedSub || subCatId, all_categories, all_subCategories);
                }
                if (pType) pType = pType.toLowerCase() === "software" ? "Software" : "Hardware";
                else pType = "Hardware";

                setPayload({
                    name: data.name || "",
                    slug: data.slug || "",
                    mfg_id: mfgId,
                    productType: pType,
                    categoryId: matchedCat?._id || matchedCat?.id || resolvedCatId,
                    subCategoryId: matchedSub?._id || matchedSub?.id || subCatId,
                    description: data.description || data.short_description || "",
                    long_description: data.long_description || data.longDescription || "",
                    imageUrl: data.imageUrl || data.image || "",
                    tempImageFile: null
                });

                // Match Manufacturer
                const source = searchedMfg?.length > 0 ? searchedMfg : all_manufacturers;
                const foundMfg = source?.find((m) => (m._id && String(m._id) === String(mfgId)) || (m.id && String(m.id) === String(mfgId)) || (m.name && m.name.toLowerCase() === String(mfgId).toLowerCase()));
                if (foundMfg) {
                    setMfg(foundMfg);
                } else if (data.mfg && typeof data.mfg === "object") {
                    setMfg(data.mfg);
                } else if (data.manufacturer && typeof data.manufacturer === "object") {
                    setMfg(data.manufacturer);
                } else if (data.mfg_name) {
                    setMfg({ _id: mfgId, name: data.mfg_name });
                }

                // Extract variants array from all possible API response shapes
                const existingVariants = 
                    (Array.isArray(data.variants) && data.variants.length > 0 ? data.variants : null) || 
                    (Array.isArray(data.products) && data.products.length > 0 ? data.products : null) || 
                    (Array.isArray(data.items) && data.items.length > 0 ? data.items : null) || 
                    (Array.isArray(data.variantDetails) && data.variantDetails.length > 0 ? data.variantDetails : null) || 
                    (Array.isArray(data.relatedProducts) && data.relatedProducts.length > 0 ? data.relatedProducts : null) || 
                    [];

                // Harvest attribute keys from data.variantAttributes and individual variants
                const keysSet = new Set();
                if (Array.isArray(data.variantAttributes)) {
                    data.variantAttributes.forEach((k) => {
                        if (k && typeof k === "string") keysSet.add(k.trim());
                    });
                } else if (data.variantAttributes && typeof data.variantAttributes === "object") {
                    Object.keys(data.variantAttributes).forEach((k) => {
                        if (k && typeof k === "string") keysSet.add(k.trim());
                    });
                }
                existingVariants.forEach((v) => {
                    const parsedAttrs = parseVariantAttributes(v);
                    Object.keys(parsedAttrs).forEach((k) => {
                        if (k && typeof k === "string") keysSet.add(k.trim());
                    });
                });

                if (keysSet.size > 0) {
                    setAttributeKeys(Array.from(keysSet));
                }

                // Populate existing variants into stagedProducts
                if (existingVariants.length > 0) {
                    const staged = existingVariants.map((v, idx) => {
                        const pId = v._id || v.id || v.productId;
                        const existingAttrs = parseVariantAttributes(v);
                        return {
                            productId: pId,
                            product: v,
                            attributes: existingAttrs,
                            isExisting: true,
                            isPrimary: idx === 0
                        };
                    });
                    setStagedProducts(staged);
                }
            };

            // Initial populate with passed editData
            populateGroupData(editData);

            // Fetch fresh full group details via getProductGroup API
            const targetGroupId = editData.groupId || editData._id || editData.id;
            if (targetGroupId) {
                setFetchingDetails(true);
                getProductGroup(targetGroupId, (res) => {
                    setFetchingDetails(false);
                    if (res?.status === 200 && res?.data) {
                        const groupObj = res.data?.data || res.data?.group || res.data?.groupData || res.data;
                        const variantsList = res.data?.variants || res.data?.data?.variants || res.data?.group?.variants || res.variants;

                        const mergedData = {
                            ...(typeof groupObj === "object" ? groupObj : {}),
                            ...(Array.isArray(variantsList) ? { variants: variantsList } : {})
                        };

                        if (mergedData.groupType) {
                            setGroupType(mergedData.groupType);
                        }
                        populateGroupData(mergedData);
                    }
                });
            }
        }
    }, [editData, isOpen, all_manufacturers, all_categories, all_subCategories]);

    const handleClose = () => {
        setGroupType("variant");
        setMfg(null);
        setSearchMfg([]);
        setPrimaryProduct(null);
        setPrimarySearchResults([]);
        setProductSearchResults([]);
        setStagedProducts([]);
        setDeletedProductIds([]);
        setAttributeKeys(["capacity"]);
        setNewAttributeInput("");
        setLoadingProgressText("");
        setPayload({
            name: "",
            slug: "",
            mfg_id: "",
            productType: "",
            categoryId: "",
            subCategoryId: "",
            description: "",
            long_description: "",
            imageUrl: "",
            tempImageFile: null
        });
        if (setEditData) setEditData(null);
        toggle();
    };

    const handleChange = (key, val) => {
        setPayload((prev) => ({ ...prev, [key]: val }));
    };

    const cleanMPN = (text) => {
        if (!text || typeof text !== "string") return text;
        return text
            .replace(/,\s*MPN\s+[a-zA-Z0-9_-]+,?\s*/gi, ", ")
            .replace(/\bMPN\s*[:#-]?\s*[a-zA-Z0-9_-]+\b,?\s*/gi, "")
            .replace(/[ \t]{2,}/g, " ")
            .replace(/,\s*,/g, ",")
            .trim();
    };

    const prepareMarkdownForHTML = (text) => {
        if (!text || typeof text !== "string") return text || "";
        return text
            .replace(/([^\n])\s*(#{1,4}\s+)/g, "$1\n\n$2")
            .replace(/([^\n])\s*•\s*/g, "$1\n- ")
            .replace(/([^\n])\s+-\s+/g, "$1\n- ");
    };

    const handleHumanize = () => {
        if (!payload.long_description) {
            toast.error("Please provide a long description to humanize.");
            return;
        }
        setHumanizing(true);
        humanizeProductDescription(
            {
                mfgPartNumber: "",
                mfgName: mfg?.name || "",
                title: payload.name,
                description: cleanMPN(payload.description),
                specifications: attributeKeys.join(", "),
                longDescription: payload.long_description
            },
            (res) => {
                setHumanizing(false);
                if (res.status === 200 || res.status === 201 || res?.success) {
                    const responseBody = res.data;
                    const responseData = responseBody?.data || responseBody;
                    const rawShortDesc = responseData?.short_description || responseData?.shortDescription;
                    const rawLongDesc = responseData?.long_description || responseData?.longDescription || (typeof responseData === "string" ? responseData : "");

                    const shortDesc = cleanMPN(rawShortDesc);
                    const preparedLongDesc = prepareMarkdownForHTML(rawLongDesc);
                    let formattedLongDesc = preparedLongDesc;
                    if (formatAIResponseToHTML) {
                        formattedLongDesc = formatAIResponseToHTML(preparedLongDesc);
                    }

                    setPayload((prev) => ({
                        ...prev,
                        long_description: formattedLongDesc || prev.long_description,
                        description: shortDesc || prev.description
                    }));
                    toast.success("Description rewritten successfully!");
                } else {
                    toast.error(res.message || "Failed to rewrite description.");
                }
            }
        );
    };

    const handleSubmitMfgData = async (obj, file) => {
        setMfgSubmitLoading(true);
        let data = {
            _id: uniqueID(),
            ...obj,
            createdAt: Date.parse(new Date()),
            id: await generateCustomID("Manufacturers", "id")
        };
        let losFile;
        if (file !== undefined && typeof file === "object") {
            losFile = await uploadImage(file, "manufacturers");
            if (losFile) {
                data.loaLos = losFile;
            }
        }
        saveData("Manufacturers", data._id, data)
            .then((res) => {
                if (res) {
                    toast.success("Manufacturer Added Successfully!");
                    setMfgModal(false);
                    setMfg(data);
                    handleChange("mfg_id", data._id);
                    setSearchMfg((prev) => [data, ...prev]);
                }
                setMfgSubmitLoading(false);
            })
            .catch(() => setMfgSubmitLoading(false));
    };

    // Manufacturer select options
    const mfgOptions = useMemo(() => {
        const source = searchedMfg?.length > 0 ? searchedMfg : all_manufacturers || [];
        const list = [...source];
        if (mfg && !list.some((m) => (m._id && String(m._id) === String(mfg._id)) || (m.id && String(m.id) === String(mfg.id)))) {
            list.unshift(mfg);
        }
        return list.map((option) => ({
            value: option._id || option.id,
            label: `${option.id ? `${option.id}-` : ""}${option.name || option.mfg_name || ""}`
        }));
    }, [searchedMfg, all_manufacturers, mfg]);

    // Construct unified TreeData for Category & SubCategory (shows 100% of categories & subcategories)
    const categoryTreeData = useMemo(() => {
        const catList = Array.isArray(all_categories) ? all_categories : [];
        const subList = Array.isArray(all_subCategories) ? all_subCategories : [];

        if (catList.length === 0 && subList.length === 0) return [];

        const data = catList.map((cat) => {
            const catId = cat._id || cat.id;
            const catName = cat.categoryName || cat.category || cat.name || "Unnamed Category";
            const catType = cat.categoryType || cat.category_type || cat.type || "";
            const catKey = `cat_${catId}`;

            // Find subcategories belonging to this category
            const subCats = subList.filter((sub) => isSubInCat(sub, cat));

            return {
                title: (
                    <div
                        className="d-flex align-items-center justify-content-between py-1 pe-2 w-100"
                        style={{ minWidth: 0, gap: "10px" }}
                    >
                        <div className="d-flex align-items-center gap-2 text-truncate" style={{ minWidth: 0 }}>
                            <FiFolder className="text-primary flex-shrink-0" size={14} />
                            {cat.id && (
                                <span
                                    className="badge bg-secondary-subtle text-secondary border font-monospace flex-shrink-0"
                                    style={{ fontSize: "10px", padding: "2px 5px", fontWeight: 600 }}
                                >
                                    #{cat.id}
                                </span>
                            )}
                            <span className="fw-semibold text-dark text-truncate" style={{ fontSize: "13px" }}>
                                {catName}
                            </span>
                        </div>
                        <span
                            className="badge bg-light text-secondary border flex-shrink-0"
                            style={{ fontSize: "10px", padding: "2px 6px", fontWeight: 600, letterSpacing: "0.3px", textTransform: "uppercase" }}
                        >
                            Category
                        </span>
                    </div>
                ),
                label: `${cat.id ? `[#${cat.id}] ` : ""}${catName}`,
                value: catKey,
                key: catKey,
                searchValue: `${cat.id || ""} ${catName} ${catType} Category`,
                children: subCats.map((sub) => {
                    const subId = sub._id || sub.id;
                    const subName = sub.subCategoryName || sub.subCategory || sub.name || "Unnamed SubCategory";
                    const subKey = `sub_${subId}`;

                    return {
                        title: (
                            <div
                                className="d-flex align-items-center justify-content-between py-1 pe-2 ps-1 w-100"
                                style={{ minWidth: 0, gap: "10px" }}
                            >
                                <div className="d-flex align-items-center gap-2 text-truncate" style={{ minWidth: 0 }}>
                                    <FiCornerDownRight className="text-muted flex-shrink-0" size={12} />
                                    {sub.id && (
                                        <span
                                            className="badge bg-primary-subtle text-primary border border-primary-subtle font-monospace flex-shrink-0"
                                            style={{ fontSize: "10px", padding: "2px 5px", fontWeight: 600 }}
                                        >
                                            #{sub.id}
                                        </span>
                                    )}
                                    <span className="text-dark text-truncate" style={{ fontSize: "13px" }}>
                                        {subName}
                                    </span>
                                </div>
                                <span
                                    className="badge bg-primary-subtle text-primary border border-primary-subtle flex-shrink-0"
                                    style={{ fontSize: "10px", padding: "2px 6px", fontWeight: 600, letterSpacing: "0.3px", textTransform: "uppercase" }}
                                >
                                    Sub Category
                                </span>
                            </div>
                        ),
                        label: `${sub.id ? `[#${sub.id}] ` : ""}${subName} (${catName})`,
                        value: subKey,
                        key: subKey,
                        searchValue: `${sub.id || ""} ${subName} ${catName} Sub Category`,
                    };
                }),
            };
        });

        // Add unmapped subcategories
        const unmappedSubCats = subList.filter((sub) => !catList.some((cat) => isSubInCat(sub, cat)));
        if (unmappedSubCats.length > 0) {
            data.push({
                title: (
                    <div
                        className="d-flex align-items-center justify-content-between py-1 pe-2 w-100"
                        style={{ minWidth: 0, gap: "10px" }}
                    >
                        <div className="d-flex align-items-center gap-2 text-truncate" style={{ minWidth: 0 }}>
                            <FiFolder className="text-secondary flex-shrink-0" size={14} />
                            <span className="fw-semibold text-dark text-truncate" style={{ fontSize: "13px" }}>
                                Other Subcategories
                            </span>
                        </div>
                        <span
                            className="badge bg-light text-secondary border flex-shrink-0"
                            style={{ fontSize: "10px", padding: "2px 6px", fontWeight: 600, letterSpacing: "0.3px", textTransform: "uppercase" }}
                        >
                            Group
                        </span>
                    </div>
                ),
                label: "Other Subcategories",
                value: "group_unmapped",
                key: "group_unmapped",
                disabled: true,
                selectable: false,
                searchValue: "Other Subcategories",
                children: unmappedSubCats.map((sub) => {
                    const subId = sub._id || sub.id;
                    const subName = sub.subCategoryName || sub.subCategory || sub.name || "Unnamed SubCategory";
                    const subKey = `sub_${subId}`;

                    return {
                        title: (
                            <div
                                className="d-flex align-items-center justify-content-between py-1 pe-2 ps-1 w-100"
                                style={{ minWidth: 0, gap: "10px" }}
                            >
                                <div className="d-flex align-items-center gap-2 text-truncate" style={{ minWidth: 0 }}>
                                    <FiCornerDownRight className="text-muted flex-shrink-0" size={12} />
                                    {sub.id && (
                                        <span
                                            className="badge bg-primary-subtle text-primary border border-primary-subtle font-monospace flex-shrink-0"
                                            style={{ fontSize: "10px", padding: "2px 5px", fontWeight: 600 }}
                                        >
                                            #{sub.id}
                                        </span>
                                    )}
                                    <span className="text-dark text-truncate" style={{ fontSize: "13px" }}>
                                        {subName}
                                    </span>
                                </div>
                                <span
                                    className="badge bg-primary-subtle text-primary border border-primary-subtle flex-shrink-0"
                                    style={{ fontSize: "10px", padding: "2px 6px", fontWeight: 600, letterSpacing: "0.3px", textTransform: "uppercase" }}
                                >
                                    Sub Category
                                </span>
                            </div>
                        ),
                        label: `${sub.id ? `[#${sub.id}] ` : ""}${subName}`,
                        value: subKey,
                        key: subKey,
                        searchValue: `${sub.id || ""} ${subName} Sub Category`,
                    };
                }),
            });
        }

        return data;
    }, [all_categories, all_subCategories]);

    // Compute currently selected value in TreeSelect
    const selectedTreeValue = useMemo(() => {
        if (payload.subCategoryId) {
            const id = typeof payload.subCategoryId === "object"
                ? (payload.subCategoryId._id || payload.subCategoryId.id)
                : payload.subCategoryId;
            if (id) return `sub_${id}`;
        }
        if (payload.categoryId) {
            const id = typeof payload.categoryId === "object"
                ? (payload.categoryId._id || payload.categoryId.id)
                : payload.categoryId;
            if (id) return `cat_${id}`;
        }
        return undefined;
    }, [payload.categoryId, payload.subCategoryId]);

    // Handle single input selection
    const handleCategoryTreeChange = (val) => {
        if (!val) {
            setPayload((prev) => ({
                ...prev,
                categoryId: "",
                subCategoryId: ""
            }));
            return;
        }

        const valStr = String(val);
        if (valStr.startsWith("cat_")) {
            const catId = valStr.replace("cat_", "");
            const matchedCat = all_categories?.find(
                (c) => String(c._id) === catId || String(c.id) === catId
            );
            const catType = matchedCat?.categoryType || matchedCat?.category_type;
            const autoProductType = catType
                ? (String(catType).toLowerCase() === "software" ? "Software" : "Hardware")
                : "";

            setPayload((prev) => ({
                ...prev,
                categoryId: catId,
                subCategoryId: "",
                productType: autoProductType || prev.productType || "Hardware"
            }));
        } else if (valStr.startsWith("sub_")) {
            const subId = valStr.replace("sub_", "");
            const matchedSub = all_subCategories?.find(
                (s) => String(s._id) === subId || String(s.id) === subId
            );

            let matchedCatId = "";
            let matchedCat = null;
            if (matchedSub) {
                if (all_categories?.length > 0) {
                    matchedCat = all_categories.find((c) => isSubInCat(matchedSub, c));
                }
                matchedCatId = matchedCat?._id || matchedCat?.id || "";
            }

            const subType = matchedCat?.categoryType || matchedCat?.category_type || matchedSub?.categoryType || matchedSub?.category_type;
            const autoProductType = subType
                ? (String(subType).toLowerCase() === "software" ? "Software" : "Hardware")
                : getProductTypeFromSubCat(matchedSub || subId, all_categories, all_subCategories);

            setPayload((prev) => ({
                ...prev,
                subCategoryId: subId,
                categoryId: matchedCatId || prev.categoryId,
                productType: autoProductType || prev.productType || "Hardware"
            }));
        }
    };

    // Unified Power Submission Orchestrator
    const onSubmit = async () => {
        const resolvedGroupName = payload.name?.trim() || primaryProduct?.title || primaryProduct?.name || stagedProducts[0]?.product?.title || stagedProducts[0]?.product?.name || "";
        if (!resolvedGroupName) {
            toast.error("Group Name is required!");
            return;
        }

        if (isVariant && !payload.productType) {
            toast.error("Product Type is required!");
            return;
        }

        // Validate attribute completeness for variant groups
        if (isVariant && attributeKeys.length > 0 && stagedProducts.length > 0) {
            for (let i = 0; i < stagedProducts.length; i++) {
                const item = stagedProducts[i];
                const pTitle = item.product.title || item.product.name || `Product #${i + 1}`;
                for (let key of attributeKeys) {
                    if (!item.attributes?.[key]?.trim()) {
                        toast.error(`Please provide "${key}" for "${pTitle}".`);
                        return;
                    }
                }
            }
        }

        setLoading(true);

        // Upload new image if file selected
        let submitImageUrl = payload.imageUrl || "";
        try {
            if (payload.tempImageFile) {
                setLoadingProgressText("Uploading group image...");
                const url = await uploadImage(payload.tempImageFile, "product-groups");
                submitImageUrl = url;
            }
        } catch (error) {
            toast.error("Failed to upload group image.");
            setLoading(false);
            setLoadingProgressText("");
            return;
        }

        const catId = payload.categoryId ? String(payload.categoryId._id || payload.categoryId.id || payload.categoryId) : "";
        const subCatId = payload.subCategoryId ? String(payload.subCategoryId._id || payload.subCategoryId.id || payload.subCategoryId) : "";
        const mfgId = payload.mfg_id ? String(payload.mfg_id._id || payload.mfg_id.id || payload.mfg_id) : "";

        if (isEditMode) {
            // ==================== EDIT GROUP FLOW ====================
            const targetGroupId = editData.groupId || editData._id || editData.id;
            const updatePayload = {
                groupId: targetGroupId,
                groupType,
                name: resolvedGroupName,
                slug: payload.slug || undefined,
                mfg_id: mfgId || "",
                productType: payload.productType || "",
                categoryId: catId || "",
                category: catId ? { _id: catId } : undefined,
                subCategoryId: subCatId || "",
                subCategory: subCatId ? { _id: subCatId } : undefined,
                description: payload.description || "",
                long_description: payload.long_description || "",
                imageUrl: submitImageUrl,
                ...(isVariant && { variantAttributes: attributeKeys })
            };

            setLoadingProgressText("Updating group details...");
            updateProductGroup(updatePayload, async (res) => {
                if (res.status === 200 || res.status === 201 || res?.success) {
                    try {
                        // 1. Remove deleted variants
                        if (deletedProductIds.length > 0) {
                            setLoadingProgressText(`Removing ${deletedProductIds.length} detached products...`);
                            for (let pId of deletedProductIds) {
                                await new Promise((resolve) => {
                                    removeVariantFromGroup({ groupId: targetGroupId, productId: pId }, () => resolve());
                                });
                            }
                        }

                        // 2. Add newly staged variants
                        const newProducts = stagedProducts.filter((p) => !p.isExisting);
                        if (newProducts.length > 0) {
                            for (let i = 0; i < newProducts.length; i++) {
                                const item = newProducts[i];
                                const pId = item.product._id || item.product.id || item.productId;
                                setLoadingProgressText(`Attaching new product ${i + 1} of ${newProducts.length}...`);
                                
                                const cleanAttrs = {};
                                if (isVariant) {
                                    attributeKeys.forEach((key) => {
                                        const lower = key.toLowerCase();
                                        cleanAttrs[key] = item.attributes?.[key] ?? item.attributes?.[lower] ?? "";
                                    });
                                }

                                await new Promise((resolve) => {
                                    addVariantToGroup(
                                        {
                                            groupId: targetGroupId,
                                            productId: pId,
                                            variantAttributes: cleanAttrs,
                                            sortOrder: stagedProducts.indexOf(item)
                                        },
                                        () => resolve()
                                    );
                                });
                            }
                        }

                        // 3. Update existing variants attributes if variant group
                        if (isVariant) {
                            const existingProducts = stagedProducts.filter((p) => p.isExisting);
                            if (existingProducts.length > 0) {
                                setLoadingProgressText("Updating variant attributes...");
                                const updates = existingProducts.map((item) => {
                                    const cleanAttrs = {};
                                    attributeKeys.forEach((key) => {
                                        const lower = key.toLowerCase();
                                        cleanAttrs[key] = item.attributes?.[key] ?? item.attributes?.[lower] ?? "";
                                    });
                                    return {
                                        productId: item.product._id || item.product.id || item.productId,
                                        variantAttributes: cleanAttrs
                                    };
                                });
                                await new Promise((resolve) => {
                                    bulkEditVariantAttributes({ groupId: targetGroupId, updates }, () => resolve());
                                });
                            }
                        }

                        // 4. Update order sequence
                        if (stagedProducts.length > 0) {
                            const reorderedIds = stagedProducts.map((p) => p.product?._id || p.product?.id || p.productId);
                            await new Promise((resolve) => {
                                reorderVariants({ groupId: targetGroupId, variantIds: reorderedIds }, () => resolve());
                            });
                        }

                        toast.success("Product Group and all products updated successfully!");
                        refreshData();
                        handleClose();
                    } catch (err) {
                        toast.error(err.message || "Group updated with partial variant errors.");
                        refreshData();
                        handleClose();
                    }
                } else {
                    toast.error(res.message || "Failed to update group.");
                }
                setLoading(false);
                setLoadingProgressText("");
            });
        } else {
            // ==================== CREATE GROUP FLOW ====================
            if (stagedProducts.length > 0) {
                // If products are staged, create atomically via proven migrateToGroup endpoint
                setLoadingProgressText("Creating product group and linking products...");
                const primaryItem = stagedProducts[0];
                const primaryId = primaryItem?.product?._id || primaryItem?.product?.id || primaryItem?.productId;
                const additionalIds = stagedProducts.slice(1).map((p) => p.product?._id || p.product?.id || p.productId);

                let variantAttributesPayload = [];
                if (isVariant) {
                    variantAttributesPayload = stagedProducts.map((p, idx) => {
                        const pId = p.product?._id || p.product?.id || p.productId;
                        const cleanAttrs = {};
                        attributeKeys.forEach((key) => {
                            const lower = key.toLowerCase();
                            cleanAttrs[key] = p.attributes?.[key] ?? p.attributes?.[lower] ?? "";
                        });
                        return {
                            productId: pId,
                            attributes: cleanAttrs,
                            sortOrder: idx
                        };
                    });
                }

                const migrationPayload = {
                    groupType,
                    primaryProductId: primaryId,
                    productIds: additionalIds,
                    mfg_id: mfgId || "",
                    productType: payload.productType || "",
                    categoryId: catId || "",
                    category: catId ? { _id: catId } : undefined,
                    subCategoryId: subCatId || "",
                    subCategory: subCatId ? { _id: subCatId } : undefined,
                    name: resolvedGroupName,
                    slug: payload.slug || undefined,
                    description: payload.description || "",
                    long_description: payload.long_description || "",
                    imageUrl: submitImageUrl,
                    ...(isVariant && { variantAttributes: variantAttributesPayload })
                };

                migrateToGroup(migrationPayload, (res) => {
                    if (res.status === 200 || res.status === 201 || res?.success) {
                        toast.success(`Product Group created with ${stagedProducts.length} ${itemsPluralLabel.toLowerCase()}!`);
                        refreshData();
                        handleClose();
                    } else {
                        toast.error(res.message || "Failed to create group.");
                    }
                    setLoading(false);
                    setLoadingProgressText("");
                });
            } else {
                // Empty group creation
                setLoadingProgressText("Creating product group...");
                const createPayload = {
                    groupType,
                    name: resolvedGroupName,
                    slug: payload.slug || undefined,
                    mfg_id: mfgId || "",
                    productType: payload.productType || "",
                    categoryId: catId || "",
                    category: catId ? { _id: catId } : undefined,
                    subCategoryId: subCatId || "",
                    subCategory: subCatId ? { _id: subCatId } : undefined,
                    description: payload.description || "",
                    long_description: payload.long_description || "",
                    imageUrl: submitImageUrl,
                    ...(isVariant && { variantAttributes: attributeKeys })
                };

                createProductGroup(createPayload, (res) => {
                    if (res.status === 200 || res.status === 201 || res?.success) {
                        toast.success("Product Group created successfully!");
                        refreshData();
                        handleClose();
                    } else {
                        toast.error(res.message || "Failed to create group.");
                    }
                    setLoading(false);
                    setLoadingProgressText("");
                });
            }
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            backdrop="static"
            keyboard={false}
            centered
            className="modal-dialog-scrollable"
            style={{ maxWidth: "1400px", width: "95%" }}
        >
            <ModalBody className="p-4 bg-white" style={{ maxHeight: "90vh", overflowY: "auto" }}>
                {/* Header Section */}
                <div className="d-flex justify-content-between align-items-center mb-4 pb-3 border-bottom flex-wrap gap-3">
                    <div className="d-flex align-items-center gap-3">
                        <div className="bg-primary-soft p-3 rounded-circle shadow-sm text-primary">
                            {isVariant ? <FiBox size={24} /> : <FiLink2 size={24} />}
                        </div>
                        <div>
                            <h4 className="fw-bold text-dark mb-0">
                                {isEditMode ? `Edit ${groupTypeLabel}` : `Create ${groupTypeLabel}`}
                            </h4>
                            <p className="text-muted small mb-0 mt-1">
                                {isEditMode
                                    ? "Update group info, add or remove products, and edit variant attributes all in one place."
                                    : "Fill basic info, set attributes, and stage all products for bulk creation."}
                            </p>
                        </div>
                    </div>

                    <div className="d-flex align-items-center gap-2">
                        {/* Group Type Toggle - only allowed in Create Mode */}
                        {!isEditMode ? (
                            <div className="btn-group bg-light p-1 rounded-3 border" role="group">
                                <button
                                    type="button"
                                    onClick={() => setGroupType("variant")}
                                    className={`btn btn-sm px-3 py-1 fw-bold rounded-2 transition-all ${
                                        isVariant ? "bg-white text-primary shadow-sm" : "text-muted border-0 bg-transparent"
                                    }`}
                                    style={{ fontSize: "12px", border: isVariant ? "1px solid #e2e8f0" : "none" }}
                                >
                                    <FiBox className="me-1" /> Variant Group
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setGroupType("related")}
                                    className={`btn btn-sm px-3 py-1 fw-bold rounded-2 transition-all ${
                                        isRelated ? "bg-white text-success shadow-sm" : "text-muted border-0 bg-transparent"
                                    }`}
                                    style={{ fontSize: "12px", border: isRelated ? "1px solid #e2e8f0" : "none" }}
                                >
                                    <FiLink2 className="me-1" /> Related Group
                                </button>
                            </div>
                        ) : (
                            <Badge color={isVariant ? "primary" : "success"} pill className="px-3 py-2 fw-bold" style={{ fontSize: "12px" }}>
                                {isVariant ? <FiBox className="me-1" /> : <FiLink2 className="me-1" />}
                                {groupTypeLabel}
                            </Badge>
                        )}

                        <IoCloseSharp size={28} className="pointer text-muted ms-2" onClick={handleClose} />
                    </div>
                </div>

                {fetchingDetails ? (
                    <div className="d-flex flex-column justify-content-center align-items-center py-5 my-5">
                        <Spin size="large" />
                        <span className="text-muted mt-3 fw-bold">Loading group and variant details...</span>
                    </div>
                ) : (
                    <div>
                        {/* SECTION 1: PRIMARY PRODUCT & AUTO-FILL (Create Mode) */}
                        {!isEditMode && (
                            <Card className="border-0 shadow-sm mb-4" style={{ backgroundColor: "#f0f7ff", border: "1px solid #cce4ff" }}>
                                <CardBody className="p-3 p-md-4">
                                    <div className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom flex-wrap gap-2">
                                        <div className="d-flex align-items-center gap-2">
                                            <div className="bg-primary p-2 rounded-3 text-white">
                                                <HiSparkles size={16} />
                                            </div>
                                            <div>
                                                <h6 className="fw-bold text-dark mb-0 text-uppercase tracking-wider" style={{ fontSize: "13px" }}>
                                                    QUICK START: AUTO-FILL FROM PRIMARY PRODUCT
                                                </h6>
                                                <small className="text-muted">
                                                    Select a base product to instantly populate Name, Manufacturer, Categories, Image, and Descriptions.
                                                </small>
                                            </div>
                                        </div>
                                        {primaryProduct && (
                                            <Badge color="success" pill className="px-3 py-2">
                                                <FiCheck className="me-1" /> Auto-Filled
                                            </Badge>
                                        )}
                                    </div>

                                    <Row className="g-3 align-items-center">
                                        <Col md={12}>
                                            <Select
                                                size="large"
                                                showSearch
                                                placeholder="Type to search primary product by name, SKU, or MPN..."
                                                className="w-100"
                                                filterOption={false}
                                                onSearch={handleSearchPrimary}
                                                notFoundContent={searchingPrimary ? <Spin size="small" /> : null}
                                                value={primaryProduct ? primaryProduct._id || primaryProduct.id : undefined}
                                                onSelect={handleSelectPrimaryProduct}
                                                options={primarySearchResults.map((p) => ({
                                                    value: p._id || p.id,
                                                    label: `${p.title || p.name || "Unnamed Product"} (${p.mfgPartNumber || p.sku || "No SKU"})`,
                                                    product: p
                                                }))}
                                                optionRender={(option) => {
                                                    const p = option.data.product;
                                                    return (
                                                        <div className="d-flex align-items-center justify-content-between py-1 gap-2">
                                                            <div className="d-flex align-items-center gap-2 text-truncate" style={{ minWidth: 0 }}>
                                                                {p?.imageUrl || p?.image || p?.images?.[0] ? (
                                                                    <img
                                                                        src={p.imageUrl || p.image || p.images[0]}
                                                                        alt=""
                                                                        style={{ width: 28, height: 28, objectFit: "contain" }}
                                                                        className="rounded border bg-white flex-shrink-0"
                                                                    />
                                                                ) : (
                                                                    <div
                                                                        className="bg-light border rounded d-flex align-items-center justify-content-center text-muted flex-shrink-0"
                                                                        style={{ width: 28, height: 28 }}
                                                                    >
                                                                        <FiImage size={14} />
                                                                    </div>
                                                                )}
                                                                <div className="d-flex flex-column text-truncate" style={{ minWidth: 0 }}>
                                                                    <span className="fw-bold text-truncate" style={{ fontSize: "13px", lineHeight: "1.2" }}>
                                                                        {p?.title || p?.name || "Unnamed Product"}
                                                                    </span>
                                                                    <span className="text-muted small" style={{ fontSize: "11px", lineHeight: "1.1" }}>
                                                                        SKU: {p?.mfgPartNumber || p?.sku || "No SKU"} • ${p?.price || 0}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                            <Badge color="primary" pill className="small flex-shrink-0">
                                                                Select & Fill
                                                            </Badge>
                                                        </div>
                                                    );
                                                }}
                                            />
                                        </Col>
                                    </Row>
                                </CardBody>
                            </Card>
                        )}

                        {/* SECTION 2: BASIC INFORMATION & CATEGORIZATION */}
                        <Card className="border shadow-sm mb-4 bg-white">
                            <CardBody className="p-3 p-md-4">
                                <div className="d-flex align-items-center gap-2 mb-3 pb-2 border-bottom">
                                    <div className="bg-primary-soft p-2 rounded-3 text-primary">
                                        <FiTag size={16} />
                                    </div>
                                    <h6 className="fw-bold text-dark mb-0 text-uppercase tracking-wider" style={{ fontSize: "13px" }}>
                                        GROUP INFORMATION & CATEGORIZATION
                                    </h6>
                                </div>

                                <Row className="g-3">
                                    {/* Group Name & Slug - displayed for both Variant and Related Groups */}
                                    <Col md={8}>
                                        <FormInput
                                            type="text"
                                            label={isRelated ? "Related Group Name *" : "Group Name *"}
                                            placeholder={isRelated ? "e.g. Dell Latitude 5530 Accessories & Related Items" : "e.g. Samsung 990 PRO Series SSD"}
                                            value={payload.name}
                                            handleChange={(e) => {
                                                const newName = e.target.value;
                                                handleChange("name", newName);
                                                if (!isEditMode && (!payload.slug || payload.slug === slugify(payload.name))) {
                                                    handleChange("slug", slugify(newName));
                                                }
                                            }}
                                            size="large"
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <label className="fw-bold mb-1" style={{ fontSize: "13px" }}>Group Slug</label>
                                        <AntInput
                                            size="large"
                                            placeholder="Auto-generated or custom slug"
                                            value={payload.slug}
                                            onChange={(e) => handleChange("slug", e.target.value)}
                                        />
                                    </Col>

                                    {/* Manufacturer, ProductType, SubCat, Cat - only displayed for Variant Group */}
                                    {isVariant && (
                                        <>
                                            <Col md={4}>
                                                <div className="d-flex justify-content-between align-items-center mb-1">
                                                    <label className="fw-bold mb-0" style={{ fontSize: "13px" }}>Manufacturer</label>
                                                    <button
                                                        type="button"
                                                        className="btn btn-link p-0 text-primary small text-decoration-none"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            setMfgModal(true);
                                                        }}
                                                    >
                                                        + New Mfg
                                                    </button>
                                                </div>
                                                <Select
                                                    size="large"
                                                    showSearch
                                                    placeholder="Select Manufacturer"
                                                    filterOption={false}
                                                    onSelect={(val) => handleMfg(val)}
                                                    value={payload.mfg_id || undefined}
                                                    onSearch={handleManufacturerSearch}
                                                    className="w-100"
                                                    options={mfgOptions}
                                                    loading={dataLoading}
                                                    notFoundContent={dataLoading ? <Spin size="small" /> : "No manufacturers found"}
                                                />
                                            </Col>

                                            <Col md={4}>
                                                <label className="fw-bold mb-1" style={{ fontSize: "13px" }}>Product Type *</label>
                                                <Select
                                                    size="large"
                                                    placeholder="Select Product Type"
                                                    className="w-100 drop_down_style"
                                                    value={payload.productType || undefined}
                                                    options={productTypes}
                                                    onChange={(val) => {
                                                        handleChange("productType", val);
                                                    }}
                                                />
                                            </Col>

                                            <Col md={4}>
                                                <label className="fw-bold mb-1" style={{ fontSize: "13px" }}>Category / Sub Category</label>
                                                <TreeSelect
                                                    size="large"
                                                    showSearch
                                                    allowClear
                                                    treeDefaultExpandAll
                                                    treeNodeLabelProp="label"
                                                    placeholder="Select Category or Sub Category"
                                                    value={selectedTreeValue}
                                                    onChange={handleCategoryTreeChange}
                                                    className="w-100"
                                                    popupMatchSelectWidth={false}
                                                    dropdownMatchSelectWidth={false}
                                                    getPopupContainer={(trigger) => trigger.parentNode}
                                                    dropdownStyle={{
                                                        minWidth: 400,
                                                        maxWidth: 540,
                                                        maxHeight: 400,
                                                        overflowY: "auto",
                                                        borderRadius: "8px",
                                                        padding: "6px 8px",
                                                        boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.12), 0 8px 10px -6px rgba(0, 0, 0, 0.08)"
                                                    }}
                                                    treeData={categoryTreeData}
                                                    filterTreeNode={(input, treeNode) => {
                                                        const searchTarget = (treeNode?.searchValue || treeNode?.label || "").toLowerCase();
                                                        const tokens = input.toLowerCase().trim().split(/\s+/).filter(Boolean);
                                                        return tokens.every((token) => searchTarget.includes(token));
                                                    }}
                                                />
                                            </Col>
                                        </>
                                    )}

                                    {/* Image and Logistics */}
                                    <Col md={8}>
                                        <FormInput
                                            type="text"
                                            label="Group Image URL"
                                            placeholder="Paste image link"
                                            value={payload.imageUrl}
                                            handleChange={(e) => handleChange("imageUrl", e.target.value)}
                                            size="large"
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <Label className="fw-bold mb-1" style={{ fontSize: "13px" }}>Or Upload Image</Label>
                                        <div className="d-flex align-items-center gap-2">
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) => handleChange("tempImageFile", e.target.files[0])}
                                                style={{ display: "none" }}
                                                id="power-group-image-upload"
                                            />
                                            <label
                                                htmlFor="power-group-image-upload"
                                                className="btn btn-outline-primary mb-0 d-flex align-items-center justify-content-center w-100"
                                                style={{ height: "40px", cursor: "pointer" }}
                                            >
                                                {payload.tempImageFile ? "Change File" : "Select File"}
                                            </label>
                                        </div>
                                        {payload.tempImageFile && (
                                            <div className="mt-1 small text-muted text-truncate" style={{ maxWidth: "200px" }}>
                                                Selected: {payload.tempImageFile.name}
                                            </div>
                                        )}
                                    </Col>

                                    {/* Short & Long Description (for variant groups) */}
                                    {isVariant && (
                                        <>
                                            <Col md={12}>
                                                <FormInput
                                                    type="text"
                                                    label="Short Description"
                                                    placeholder="Brief marketing summary"
                                                    value={payload.description}
                                                    handleChange={(e) => handleChange("description", e.target.value)}
                                                    size="large"
                                                />
                                            </Col>
                                            <Col md={12}>
                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                    <Label className="mb-0 fw-bold" style={{ fontSize: "13px" }}>Long Description (Overview)</Label>
                                                    <Button
                                                        color="primary"
                                                        outline
                                                        size="sm"
                                                        className="py-1 px-3 d-flex align-items-center gap-1"
                                                        onClick={handleHumanize}
                                                        disabled={humanizing}
                                                    >
                                                        {humanizing ? <Spin size="small" /> : <><HiSparkles size={14} /> Rewrite by AI</>}
                                                    </Button>
                                                </div>
                                                <ReactQuill
                                                    theme="snow"
                                                    className="product-description-editor bg-white"
                                                    value={payload.long_description}
                                                    onChange={(val) => handleChange("long_description", val)}
                                                />
                                            </Col>
                                        </>
                                    )}
                                </Row>
                            </CardBody>
                        </Card>

                        {/* SECTION 3: VARIANT ATTRIBUTES CONFIGURATION (Only for Variant Groups) */}
                        {isVariant && (
                            <Card className="border shadow-sm mb-4 bg-light">
                                <CardBody className="p-3 p-md-4">
                                    <div className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom flex-wrap gap-2">
                                        <div className="d-flex align-items-center gap-2">
                                            <div className="bg-primary-soft p-2 rounded-3 text-primary">
                                                <FiList size={16} />
                                            </div>
                                            <div>
                                                <h6 className="fw-bold text-dark mb-0 text-uppercase tracking-wider" style={{ fontSize: "13px" }}>
                                                    VARIANT ATTRIBUTE TAGS
                                                </h6>
                                                <small className="text-muted">Define the variation dimensions for this group (e.g. Capacity, Color, Speed).</small>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Suggested Tags Shortcut */}
                                    <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
                                        <span className="small fw-bold text-muted">Quick Add Suggestions:</span>
                                        {SUGGESTED_ATTRIBUTES.map((sug) => {
                                            const isAdded = attributeKeys.includes(sug);
                                            return (
                                                <button
                                                    key={sug}
                                                    type="button"
                                                    disabled={isAdded}
                                                    onClick={() => handleAddAttributeKey(sug)}
                                                    className={`btn btn-sm py-0 px-2 rounded-pill ${
                                                        isAdded ? "btn-light text-muted border" : "btn-outline-primary"
                                                    }`}
                                                    style={{ fontSize: "12px" }}
                                                >
                                                    + {sug}
                                                </button>
                                            );
                                        })}
                                    </div>

                                    {/* Custom Tag Input */}
                                    <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
                                        <div className="d-flex align-items-center gap-2" style={{ maxWidth: "320px" }}>
                                            <AntInput
                                                placeholder="Custom attribute name (e.g. connector)..."
                                                value={newAttributeInput}
                                                onChange={(e) => setNewAttributeInput(e.target.value)}
                                                onPressEnter={() => handleAddAttributeKey(newAttributeInput)}
                                            />
                                            <Button
                                                color="primary"
                                                size="sm"
                                                className="px-3"
                                                onClick={() => handleAddAttributeKey(newAttributeInput)}
                                            >
                                                Add
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Active Attribute Pills */}
                                    <div className="d-flex align-items-center gap-2 flex-wrap p-2 rounded bg-white border">
                                        <span className="small fw-bold text-muted me-1">Active Attributes ({attributeKeys.length}):</span>
                                        {attributeKeys.length === 0 ? (
                                            <span className="text-muted small fst-italic">No attributes defined. Click suggestions above or add custom ones.</span>
                                        ) : (
                                            attributeKeys.map((attrKey) => (
                                                <Badge
                                                    key={attrKey}
                                                    color="primary"
                                                    className="d-flex align-items-center gap-2 px-3 py-2 rounded-pill fs-12"
                                                >
                                                    <span>{attrKey}</span>
                                                    <span
                                                        className="pointer text-white-50 hover-text-white"
                                                        onClick={() => handleRemoveAttributeKey(attrKey)}
                                                        title={`Remove "${attrKey}"`}
                                                    >
                                                        ✕
                                                    </span>
                                                </Badge>
                                            ))
                                        )}
                                    </div>
                                </CardBody>
                            </Card>
                        )}

                        {/* SECTION 4: INTEGRATED PRODUCTS & VARIANTS WORKSPACE */}
                        <Card className="border shadow-sm mb-4">
                            <CardBody className="p-3 p-md-4">
                                <div className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom flex-wrap gap-2">
                                    <div className="d-flex align-items-center gap-2">
                                        <div className="bg-primary-soft p-2 rounded-3 text-primary">
                                            <FiLayers size={16} />
                                        </div>
                                        <div>
                                            <h6 className="fw-bold text-dark mb-0 text-uppercase tracking-wider" style={{ fontSize: "13px" }}>
                                                ATTACHED {itemsPluralLabel.toUpperCase()} ({stagedProducts.length})
                                            </h6>
                                            <small className="text-muted">
                                                Search, add, remove, and configure attributes for each product directly in the table below.
                                            </small>
                                        </div>
                                    </div>

                                    {stagedProducts.length > 0 && (
                                        <Button
                                            color="link"
                                            size="sm"
                                            className="text-danger p-0 text-decoration-none"
                                            onClick={() => {
                                                const existingIds = stagedProducts.filter((p) => p.isExisting).map((p) => p.product._id || p.product.id || p.productId);
                                                if (existingIds.length > 0) {
                                                    setDeletedProductIds((prev) => [...prev, ...existingIds]);
                                                }
                                                setStagedProducts([]);
                                                setPrimaryProduct(null);
                                            }}
                                        >
                                            Clear All Products
                                        </Button>
                                    )}
                                </div>

                                {/* Instant Search & Add Product Dropdown */}
                                <div className="mb-4">
                                    <Label className="fw-bold mb-1" style={{ fontSize: "13px" }}>
                                        Search & Add Product to {groupTypeLabel}
                                    </Label>
                                    <Select
                                        size="large"
                                        showSearch
                                        placeholder={`Search by product name, SKU, or part number to add to this ${groupTypeLabel.toLowerCase()}...`}
                                        className="w-100"
                                        filterOption={false}
                                        onSearch={handleSearchAdditionalProducts}
                                        notFoundContent={searchingProducts ? <Spin size="small" /> : null}
                                        value={null}
                                        onSelect={handleAddProductToStaged}
                                        options={productSearchResults.map((p) => {
                                            const pId = p._id || p.id;
                                            const isAlreadyStaged = stagedProducts.some((sp) => (sp.product._id || sp.product.id || sp.productId) === pId);
                                            return {
                                                value: pId,
                                                label: `${p.title || p.name || "Unnamed Product"} (${p.mfgPartNumber || p.sku || "No SKU"})`,
                                                disabled: isAlreadyStaged,
                                                product: p,
                                                isAlreadyStaged
                                            };
                                        })}
                                        optionRender={(option) => {
                                            const p = option.data.product;
                                            const isAlreadyStaged = option.data.isAlreadyStaged;
                                            return (
                                                <div className="d-flex align-items-center justify-content-between py-1 gap-2">
                                                    <div className="d-flex align-items-center gap-2 text-truncate" style={{ minWidth: 0 }}>
                                                        {p?.imageUrl || p?.image || p?.images?.[0] ? (
                                                            <img
                                                                src={p.imageUrl || p.image || p.images[0]}
                                                                alt=""
                                                                style={{ width: 28, height: 28, objectFit: "contain" }}
                                                                className="rounded border bg-white flex-shrink-0"
                                                            />
                                                        ) : (
                                                            <div
                                                                className="bg-light border rounded d-flex align-items-center justify-content-center text-muted flex-shrink-0"
                                                                style={{ width: 28, height: 28 }}
                                                            >
                                                                <FiImage size={14} />
                                                            </div>
                                                        )}
                                                        <div className="d-flex flex-column text-truncate" style={{ minWidth: 0 }}>
                                                            <span className="fw-bold text-truncate" style={{ fontSize: "13px", lineHeight: "1.2" }}>
                                                                {p?.title || p?.name || "Unnamed Product"}
                                                            </span>
                                                            <span className="text-muted small" style={{ fontSize: "11px", lineHeight: "1.1" }}>
                                                                SKU: {p?.mfgPartNumber || p?.sku || "No SKU"} • Stock: {p?.stc_quantity ?? p?.total_quantity ?? p?.quantity ?? 0} • ${p?.price || 0}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    {isAlreadyStaged ? (
                                                        <Badge color="info" className="small flex-shrink-0">
                                                            Added Below
                                                        </Badge>
                                                    ) : (
                                                        <Badge color="primary" pill className="small flex-shrink-0">
                                                            + Add
                                                        </Badge>
                                                    )}
                                                </div>
                                            );
                                        }}
                                    />
                                </div>

                                {/* Live Staged Products Table */}
                                {stagedProducts.length > 0 ? (
                                    <div className="table-responsive rounded border" style={{ maxHeight: "420px", overflowY: "auto" }}>
                                        <Table hover className="mb-0 align-middle" style={{ fontSize: "13px" }}>
                                            <thead className="bg-light table-light sticky-top">
                                                <tr>
                                                    <th style={{ width: "80px" }}># / Order</th>
                                                    <th style={{ minWidth: "200px" }}>Product Details</th>
                                                    <th style={{ width: "90px" }}>Price</th>
                                                    <th style={{ width: "80px" }}>Stock</th>
                                                    {isVariant &&
                                                        attributeKeys.map((attrKey) => (
                                                            <th key={attrKey} style={{ minWidth: "140px" }}>
                                                                <div className="d-flex align-items-center justify-content-between gap-1 text-primary fw-bold">
                                                                    <div className="d-flex align-items-center gap-1 text-truncate" title={attrKey}>
                                                                        <FiTag size={12} />
                                                                        <span>{attrKey}</span>
                                                                    </div>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-link p-0 text-decoration-none small text-muted hover-text-primary"
                                                                        onClick={() => handleApplyToAll(attrKey)}
                                                                        title={`Copy first row's value to all for "${attrKey}"`}
                                                                        style={{ fontSize: "11px" }}
                                                                    >
                                                                        Fill All ↓
                                                                    </button>
                                                                </div>
                                                            </th>
                                                        ))}
                                                    <th style={{ width: "80px" }}>Condition</th>
                                                    <th style={{ width: "70px" }} className="text-end">Remove</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {stagedProducts.map((item, idx) => {
                                                    const p = item.product;
                                                    const pId = p._id || p.id || item.productId;
                                                    const isDragging = draggedIndex === idx;
                                                    const isDragOver = dragOverIndex === idx;
                                                    return (
                                                        <tr
                                                            key={pId || idx}
                                                            draggable
                                                            onDragStart={(e) => handleDragStart(e, idx)}
                                                            onDragOver={(e) => handleDragOver(e, idx)}
                                                            onDrop={(e) => handleDrop(e, idx)}
                                                            onDragEnd={handleDragEnd}
                                                            style={{
                                                                cursor: "grab",
                                                                opacity: isDragging ? 0.35 : 1,
                                                                backgroundColor: isDragOver ? "#e8f4fd" : undefined,
                                                                borderTop: isDragOver ? "2px solid #0d6efd" : undefined,
                                                                transition: "background-color 0.15s ease"
                                                            }}
                                                        >
                                                            <td>
                                                                <div className="d-flex align-items-center gap-1">
                                                                    <FiMove size={15} className="text-muted pointer me-1" title="Drag to reorder" />
                                                                    <Badge
                                                                        color={item.isPrimary ? "primary" : "light"}
                                                                        className={item.isPrimary ? "text-white px-2 py-1" : "text-dark border px-2 py-1"}
                                                                    >
                                                                        #{idx + 1} {item.isPrimary && "★"}
                                                                    </Badge>
                                                                    <div className="d-flex flex-column gap-0">
                                                                        <Button
                                                                            color="link"
                                                                            size="sm"
                                                                            className="p-0 text-muted"
                                                                            disabled={idx === 0}
                                                                            onClick={() => moveProduct(idx, idx - 1)}
                                                                            title="Move Up"
                                                                        >
                                                                            <FiChevronUp size={14} />
                                                                        </Button>
                                                                        <Button
                                                                            color="link"
                                                                            size="sm"
                                                                            className="p-0 text-muted"
                                                                            disabled={idx === stagedProducts.length - 1}
                                                                            onClick={() => moveProduct(idx, idx + 1)}
                                                                            title="Move Down"
                                                                        >
                                                                            <FiChevronDown size={14} />
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div className="d-flex align-items-center gap-2">
                                                                    {p?.imageUrl || p?.image || p?.images?.[0] ? (
                                                                        <img
                                                                            src={p.imageUrl || p.image || p.images[0]}
                                                                            alt=""
                                                                            style={{ width: 34, height: 34, objectFit: "contain" }}
                                                                            className="rounded border bg-white p-1 flex-shrink-0"
                                                                        />
                                                                    ) : (
                                                                        <div
                                                                            className="bg-light border rounded d-flex align-items-center justify-content-center text-muted flex-shrink-0"
                                                                            style={{ width: 34, height: 34 }}
                                                                        >
                                                                            <FiImage size={16} />
                                                                        </div>
                                                                    )}
                                                                    <div className="d-flex flex-column text-truncate" style={{ minWidth: 0 }}>
                                                                        <span className="fw-bold text-dark text-truncate" style={{ fontSize: "13px" }} title={p.title || p.name}>
                                                                            {p.title || p.name || "Unnamed Product"}
                                                                        </span>
                                                                        <span className="text-muted small" style={{ fontSize: "11px" }}>
                                                                            SKU: {p.mfgPartNumber || p.sku || "N/A"}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="fw-bold text-success">${p.price || 0}</td>
                                                            <td>
                                                                <Badge color="light" className="text-dark border">
                                                                    {p.stc_quantity ?? p.total_quantity ?? p.quantity ?? p.qty ?? 0}
                                                                </Badge>
                                                            </td>

                                                            {/* Editable Attribute Inputs */}
                                                            {isVariant &&
                                                                attributeKeys.map((attrKey) => {
                                                                    const currentVal = getAttributeValue(item.attributes, attrKey);
                                                                    return (
                                                                        <td key={attrKey}>
                                                                            <AntInput
                                                                                size="small"
                                                                                placeholder={`Enter ${attrKey}`}
                                                                                value={currentVal}
                                                                                onChange={(e) => handleProductAttributeChange(pId, attrKey, e.target.value)}
                                                                                style={{
                                                                                    fontSize: "12px",
                                                                                    backgroundColor: currentVal ? "#f8fbff" : "#fff",
                                                                                    borderColor: currentVal ? "#b6d4fe" : "#dee2e6"
                                                                                }}
                                                                            />
                                                                        </td>
                                                                    );
                                                                })}

                                                            <td>
                                                                <Badge color="light" className="text-secondary border">
                                                                    {p.condition || "N/A"}
                                                                </Badge>
                                                            </td>
                                                            <td className="text-end">
                                                                <Button
                                                                    color="link"
                                                                    className="text-danger p-1"
                                                                    onClick={() => handleRemoveStagedProduct(pId)}
                                                                    title="Remove from group"
                                                                >
                                                                    <FiTrash2 size={16} />
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </Table>
                                    </div>
                                ) : (
                                    <div className="text-center text-muted py-4 bg-light rounded border border-dashed">
                                        <FiBox size={32} className="mb-2 text-secondary" />
                                        <p className="mb-1 fw-bold">No products attached to this group yet.</p>
                                        <p className="small mb-0">Use the search box above to search and stage products into this group.</p>
                                    </div>
                                )}
                            </CardBody>
                        </Card>

                        {/* SUBMIT FOOTER */}
                        <div className="d-flex justify-content-between align-items-center pt-3 border-top mt-3 flex-wrap gap-3">
                            <div className="d-flex align-items-center gap-2">
                                {loadingProgressText ? (
                                    <span className="text-primary fw-bold d-flex align-items-center gap-2">
                                        <Spinner size="sm" color="primary" /> {loadingProgressText}
                                    </span>
                                ) : (
                                    <span className="text-muted small">
                                        {stagedProducts.length} {itemsPluralLabel.toLowerCase()} ready in group.
                                    </span>
                                )}
                            </div>

                            <div className="d-flex align-items-center gap-2">
                                <GeneralButton
                                    title="Cancel"
                                    className="bg-secondary text-white shadow-sm px-4"
                                    handleClick={handleClose}
                                    disabled={loading}
                                />
                                <GeneralButton
                                    title={
                                        loading ? (
                                            loadingProgressText || "Saving..."
                                        ) : isEditMode ? (
                                            "Save Group & All Changes"
                                        ) : (
                                            `Create ${groupTypeLabel} & Link (${stagedProducts.length}) Products`
                                        )
                                    }
                                    className="bg-primary text-white shadow-sm px-4"
                                    handleClick={onSubmit}
                                    disabled={loading}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </ModalBody>

            <AddNewManufacturer
                modal={mfgModal}
                toggle={() => setMfgModal(!mfgModal)}
                handleSubmitData={handleSubmitMfgData}
                loading={mfgSubmitLoading}
            />
        </Modal>
    );
};

export default CreateProductGroupModal;
